import { Button, Checkbox, MyIcon } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import Popover from 'react-bootstrap/Popover';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { DTYPE_NULL, DTYPE_STRING } from '../DataTypesConstants';
import { namespaceAndFieldNameRegex } from '../SchemaGeneratorConstants';
import { DataTypeInterface, Field, TypeNode, Utils } from '../Utils';
import { DataTypesList } from '../datatype-list/DataTypesList';
import './FieldElementContainer.scss';

interface FieldElemContainerInterface {
  field: Field,
  fields: Field[],
  parentField: Field | null,
  updateFields: (fields: Field[]) => void,
  arrOfEncryptedData: (id: string, checked: boolean) => void,
  isCheckClicked: boolean,
  encryptDataArr: string[];
  disableAvroschemaConfirm: (disable: boolean) => void;
  isSensitiveFieldExist: boolean;
  arrayNumberChildDataTypeClick?: (dataType: DataTypeInterface, field: Field) => void;
}

const FieldElemContainer = ({ field, fields, parentField, updateFields, arrOfEncryptedData, isCheckClicked, encryptDataArr, disableAvroschemaConfirm, isSensitiveFieldExist, arrayNumberChildDataTypeClick = undefined }: FieldElemContainerInterface) => {
  const [isDatatypeBtnClick, setIsDatatypeBtnClick] = useState(false);
  const [isNamespaceInvalid, setIsNamespaceInvalid] = useState(false);
  const [isFieldNameInvalid, setIsFieldNameInvalid] = useState(false);
  const [isFieldNameRequired, setIsFieldNameRequired] = useState(false);
  const [isNamespaceRequired, setIsNamespaceRequired] = useState(false);
  const [dataTypeError, setDataTypeError] = useState(false);

  const { t: translate } = useTranslation();
  /**
   * @method toggleChildren
   * @description Toggles the visibility of child elements based on the field ID.
   * @param e - The mouse event that triggered the toggle.
   * @param fieldId - The ID of the field.
   */
  const toggleChildren = (e: React.MouseEvent<HTMLButtonElement>, fieldId: string) => {
    const element = e.currentTarget?.firstChild as HTMLElement;
    if (element) {
      element.classList.toggle('bi-caret-down-fill');
      element.classList.toggle('bi-caret-right-fill');
      const selector = `#field-${fieldId} .child-field`;
      document.querySelector(selector)?.classList.toggle('hide-child-elem');
    }
  };

  /**
   * @method dataTypeClickHandler
   * @description Handles the click event for a data type item.
   * @param dataTypeItem - The selected data type item.
   * @param field - The field associated with the data type item.
   */
  const dataTypeClickHandler = (dataTypeItem: DataTypeInterface, field: Field) => {
    const updatedFields = [...fields];
    if (field) {
      if (Utils.checkTypes(field.type, dataTypeItem.dataType)) { // If datatype item already exist then remove
        if (!(isValidFieldForEncrypt() && dataTypeItem.dataType === DTYPE_NULL)) {
          removeEncryptedField(field);
        }
        removeIfDataTypeExists(dataTypeItem, field);
      } else { // Add data type to the curent field
        removeEncryptedField(field);
        addDataTypeIfNotExists(dataTypeItem, field);
      }
      updateFields(updatedFields);
    }
  };

  /**
   * @method removeIfDataTypeExists
   * @description It will remove the data type if already exists
   * @param dataTypeItem
   * @param field
   */
  const removeIfDataTypeExists = (dataTypeItem: DataTypeInterface, field: Field) => {
    if ((typeof field.type === 'object' && !Array.isArray(field.type) && (field.type.type === 'record' || field.type.type === 'array'))
      || field.type === dataTypeItem.dataType) {
      field.type = '';
      if (field.items) {
        delete field.items;
      }
      if (field.fields) {
        delete field.fields;
      }
    } else { // if fieldType is collection of dataType
      rmFieldIfTypeIsCollection(dataTypeItem, field);
    }
    if (dataTypeItem.dataType === 'null') {
      delete field.default;
    }
  };

  /**
   * @method rmFieldIfTypeIsCollection
   * @description Method will be invoked to remove the field if Type is collection
   * @param dataTypeItem
   * @param field
   */
  const rmFieldIfTypeIsCollection = (dataTypeItem: DataTypeInterface, field: Field) => {
    const newFieldTypes = (field.type as Array<string | TypeNode>).filter((fieldType) => {
      if (dataTypeItem.dataType === 'record' || dataTypeItem.dataType === 'array') {
        return typeof fieldType !== 'object';
      }
      return fieldType !== dataTypeItem.dataType;
    });
    if (newFieldTypes.length === 1) {
      if (typeof newFieldTypes[0] === 'object') {
        const [fieldType] = newFieldTypes;
        field.type = fieldType;
      } else {
        [field.type] = newFieldTypes;
      }
    } else {
      field.type = [...newFieldTypes];
    }
  };

  /**
   * @method addDataTypeIfNotExists
   * @description Add the data type if its not exists
   * @param dataTypeItem
   * @param field
   */
  const addDataTypeIfNotExists = (dataTypeItem: DataTypeInterface, field: Field) => {
    addArrayOrRecordType(dataTypeItem, field);
    if (dataTypeItem.dataType === 'null') {
      field.default = null;
    }
  };

  /**
   * @method addArrayOrRecordType
   * @description Add if the selected data type if Array or Object
   * @param dataTypeItem
   * @param field
   */
  const addArrayOrRecordType = (dataTypeItem: DataTypeInterface, field: Field) => {
    let itemDataType: string | TypeNode = dataTypeItem.dataType;
    if (dataTypeItem.dataType === 'record') {
      itemDataType = {
        type: 'record',
        name: field.name ? field.name.charAt(0).toUpperCase() + field.name.slice(1) : `ClassName_${uuid().substring(2, 15)}`
      };
      field.type = Array.isArray(field.type) ? [...field.type, itemDataType] : [field.type, itemDataType];
    } else if (typeof field.type === 'object' && !Array.isArray(field.type) && (field.type.type === 'record' || field.type.type === 'array')) {
      //  check if existing field type is object
      if (itemDataType === 'null') {
        field.type = [
          itemDataType,
          { ...field.type }
        ];
      } else {
        field.type = [
          { ...field.type },
          itemDataType
        ];
      }
    } else if (itemDataType === 'null') {
      field.type = Array.isArray(field.type) ? [itemDataType, ...field.type] : [itemDataType, field.type];
    } else {
      field.type = Array.isArray(field.type) ? [...field.type, itemDataType] : [field.type, itemDataType];
    }
  };

  /**
  * @method addOrDeleteChildItem
  * @description Method will be invoked when user to add or delete child item
  * @param fieldItem
  * @param dataTypeItem
  */
  const addOrDeleteChildItem = (fieldItem: string | TypeNode, dataTypeItem: DataTypeInterface) => {
    if (typeof fieldItem === 'object' && fieldItem.items) {
      if (Utils.checkTypes(fieldItem.items, dataTypeItem.dataType)) {
        if (Array.isArray(fieldItem.items)) {
          const items = fieldItem.items.filter((item) => !Utils.checkTypes(item, dataTypeItem.dataType));
          fieldItem.items = [...items];
        } else {
          delete fieldItem.items;
        }
      } else {
        addArrayChildDataType(dataTypeItem, field);
      }
    }
  };

  /**
   * @method arrayChildDataTypeClickHandler
   * @description Method will be invoked when user clicks the child data type of Array data type in list
   * @param dataTypeItem
   * @param field
   */
  const arrayChildDataTypeClickHandler = (dataTypeItem: DataTypeInterface, field: Field) => {
    const updatedFields = [...fields];
    if (Utils.checkTypes(field.type, 'array')) {
      if (Array.isArray(field.type)) {
        (field.type).forEach((fieldItem: (TypeNode | string)) => {
          addOrDeleteChildItem(fieldItem, dataTypeItem);
        });
      } else {
        const fieldItem = field.type;
        addOrDeleteChildItem(fieldItem, dataTypeItem);
      }
    } else {
      addArrayChildDataType(dataTypeItem, field);
    }
    updateFields(updatedFields);
  };

  /**
   * @method getDataTypeItems
   * @description Returns the data type items based on the provided data type item.
   * If the data type is 'record', it returns an object with type 'record' and an empty name.
   * Otherwise, it returns the data type itself.
   * @param dataTypeItem The data type item to process.
   * @returns The processed data type items.
   */
  const getDataTypeItems = (dataTypeItem: DataTypeInterface) => ((dataTypeItem.dataType === 'record') ? {
    type: 'record',
    name: ''
  } : dataTypeItem.dataType);

  /**
   * @method addObjectItem
   * @description Adds an object item to the field's type array.
   * @param field - The field object.
   * @param dataTypeItem - The data type item to add.
   */
  const addObjectItem = (field: Field, dataTypeItem: DataTypeInterface) => {
    const items = getDataTypeItems(dataTypeItem);
    if (Array.isArray(field.type)) {
      field.type.forEach((item) => {
        if (typeof item === 'object' && item.type === 'array' && item.items) {
          if (items === 'null') {
            item.items = [items, ...item.items];
          } else {
            item.items = [...item.items, items];
          }
        }
      });
    } else {
      const item = field.type;
      if (typeof item === 'object' && item.type === 'array') {
        item.items = [...item.items as Array<string | TypeNode>, items];
      }
    }
  };

  /**
   * @method addArrayItem
   * @description Adds an array item to the field.
   * @param field - The field to add the array item to.
   * @param dataTypeItem - The data type of the new array item.
   */
  const addArrayItem = (field: Field, dataTypeItem: DataTypeInterface) => {
    if (Utils.checkTypes(field.type, 'array')) {
      addObjectItem(field, dataTypeItem);
    } else {
      const newItemDatatype: any = {
        type: 'array',
        id: uuid(),
        items: ''
      };
      const items = (dataTypeItem.dataType === 'record') ? {
        type: 'record',
        name: ''
      } : dataTypeItem.dataType;
      newItemDatatype.items = [items];
      field.type = (Array.isArray(field.type) ? [...field.type, newItemDatatype] : [field.type, newItemDatatype]);
    }
  };

  /**
   * @method addArrayChildDataType
   * @description Adds an array child data type to the given field.
   * @param dataTypeItem The data type to be added as an array child.
   * @param field The field to which the array child data type should be added.
   */
  const addArrayChildDataType = (dataTypeItem: DataTypeInterface, field: Field) => {
    if (field.type) {
      addArrayItem(field, dataTypeItem);
    } else {
      // Add the Same as above condition for Field type exists
      const items = getDataTypeItems(dataTypeItem);
      const newItemDatatype = {
        type: 'array',
        id: uuid(),
        items: [items]
      };
      field.type = { ...newItemDatatype };
    }
  };

  /**
   * @method arrayNumberChildDataTypeClickHandler
   * @description Handles the click event for number child data types (Integer/Double) in array context.
   * @param dataTypeItem The data type to be set as the number subtype in array items.
   * @param field The field to which the number subtype should be applied.
   */
  const arrayNumberChildDataTypeClickHandler = (dataTypeItem: DataTypeInterface, field: Field) => {
    const updatedFields = [...fields];
    // Only handle array context - don't modify main field type
    if (Utils.checkTypes(field.type, 'array')) {
      // If dataType is empty, it means deselection
      if (!dataTypeItem.dataType) {
        if (Array.isArray(field.type)) {
          (field.type).forEach((fieldItem: (TypeNode | string)) => {
            if (typeof fieldItem === 'object' && fieldItem.type === 'array' && fieldItem.items) {
              if (Array.isArray(fieldItem.items)) {
                fieldItem.items = fieldItem.items.filter((item: string | TypeNode) =>
                  item !== 'int' && item !== 'double' && (typeof item !== 'object' || (item.type !== 'int' && item.type !== 'double')));
              }
            }
          });
        } else if (typeof field.type === 'object' && field.type.type === 'array' && field.type.items) {
          if (Array.isArray(field.type.items)) {
            field.type.items = field.type.items.filter((item: string | TypeNode) =>
              item !== 'int' && item !== 'double' && (typeof item !== 'object' || (item.type !== 'int' && item.type !== 'double')));
          }
        }
        updateFields(updatedFields);
        return;
      }

      if (Array.isArray(field.type)) {
        (field.type).forEach((fieldItem: (TypeNode | string)) => {
          if (typeof fieldItem === 'object' && fieldItem.type === 'array' && fieldItem.items) {
            // Find and replace int or double in array items
            if (Array.isArray(fieldItem.items)) {
              const index = fieldItem.items.findIndex((item: string | TypeNode) =>
                item === 'int' || item === 'double' || (typeof item === 'object' && item && (item.type === 'int' || item.type === 'double')));
              if (index !== -1) {
                fieldItem.items[index] = dataTypeItem.dataType;
              } else {
                // If int/double not found, add the selected one
                fieldItem.items.push(dataTypeItem.dataType);
              }
            }
          }
        });
      } else if (typeof field.type === 'object' && field.type.type === 'array' && field.type.items) {
        // Handle single array type
        if (Array.isArray(field.type.items)) {
          const index = field.type.items.findIndex((item: string | TypeNode) =>
            item === 'int' || item === 'double' || (typeof item === 'object' && item && (item.type === 'int' || item.type === 'double')));
          if (index !== -1) {
            field.type.items[index] = dataTypeItem.dataType;
          } else {
            field.type.items.push(dataTypeItem.dataType);
          }
        }
      }
    }
    updateFields(updatedFields);
  };

  /**
   * @method numberChildDataTypeClickHandler
   * @description Handles the click event for number child data types (Integer/Double).
   * @param dataTypeItem The data type to be set as the number subtype.
   * @param field The field to which the number subtype should be applied.
   */
  const numberChildDataTypeClickHandler = (dataTypeItem: DataTypeInterface, field: Field) => {
    const updatedFields = [...fields];
    // Only handle main context - ensure we're not in array context
    const isArray = Utils.checkTypes(field.type, 'array');
    if (isArray) {
      // Don't modify if in array context - array number handler should be used instead
      return;
    }

    // If dataType is empty, it means deselection
    if (!dataTypeItem.dataType) {
      // Remove int or double from the field type
      if (Array.isArray(field.type)) {
        field.type = field.type.filter((type: string | TypeNode) =>
          type !== 'int' && type !== 'double' && (typeof type !== 'object' || (type.type !== 'int' && type.type !== 'double')));
      } else if (field.type === 'int' || field.type === 'double') {
        field.type = '';
      } else if (typeof field.type === 'object' && (field.type.type === 'int' || field.type.type === 'double')) {
        field.type = '';
      }
      updateFields(updatedFields);
      return;
    }

    const isIntOrDouble = Utils.checkTypes(field.type, 'int') || Utils.checkTypes(field.type, 'double');

    if (isIntOrDouble) {
      // If the field already has int or double type, replace it with the selected number subtype
      // Only one should be selected at a time (either 'int' OR 'double', not both)
      if (Array.isArray(field.type)) {
        // Find and replace int or double with the selected subtype
        const index = field.type.findIndex((type: string | TypeNode) =>
          type === 'int' || type === 'double'
          || (typeof type === 'object' && (type.type === 'int' || type.type === 'double')));
        if (index !== -1) {
          // Replace with the selected subtype (only one, not both)
          field.type[index] = dataTypeItem.dataType;
        }
      } else if (field.type === 'int' || field.type === 'double') {
        // Replace with the selected subtype
        field.type = dataTypeItem.dataType;
      } else if (typeof field.type === 'object' && (field.type.type === 'int' || field.type.type === 'double')) {
        field.type.type = dataTypeItem.dataType;
      }
    } else {
      // If int/double is not selected, first add int, then set the subtype
      addDataTypeIfNotExists({ dataType: 'int', id: 5, text: 'Number' }, field);
      // After adding int, if Double is selected, replace with 'double'
      if (dataTypeItem.dataType === 'double') {
        if (Array.isArray(field.type)) {
          const intIndex = field.type.findIndex((type: string | TypeNode) => type === 'int');
          if (intIndex !== -1) {
            field.type[intIndex] = 'double';
          }
        } else if (field.type === 'int') {
          field.type = 'double';
        }
      }
    }
    updateFields(updatedFields);
  };

  /**
   * @method handleLabelChange
   * @description Handles the change of label for a field.
   * @param {Field} field - The field object to update.
   * @param {string} value - The new value for the label.
   * @param {'name' | 'namespace'} key - The key indicating which property of the field to update.
   */
  const handleLabelChange = (field: Field, value: string, key: 'name' | 'namespace') => {
    const updatedFields = [...fields];
    field[key] = value;
    updateFields(updatedFields);
  };

  /**
   * @method addChildElemBtnEnable
   * @description Method will be invoked when user clicks enters data in fields
   * @returns true if invalid
   */
  const addChildElemBtnEnable = () => {
    if ((((field.namespace && field.namespace?.length > 0) || field.namespace === undefined) && field.name?.length > 0)) {
      return false;
    }
    return true;
  };

  /**
   * @method objectAddChildClickHandler
   * @description Method will be invoked when user clicks + (add child for Object data type)
   * @param field
   */
  const objectAddChildClickHandler = (field: Field) => {
    const updatedFields = [...fields];
    if (Utils.checkTypes(field.type, 'record')) {
      if (Array.isArray(field.type)) {
        addChildForObjectDTandTypeIsCollection(field);
      } else if (typeof field.type === 'object') {
        field.type.fields = field?.type?.fields ? field.type.fields : [];
        const newProperty: Field = {
          id: uuid(),
          name: '',
          type: 'string'
        };
        field.type.fields.push(newProperty);
      } else {
        field.fields = field.fields ? field.fields : [];
        const newProperty: Field = {
          id: uuid(),
          name: '',
          type: 'null',
          default: null
        };
        field.fields.push(newProperty);
      }
    }
    updateFields(updatedFields);
  };

  /**
   * @method addChildForObjectDTandTypeIsCollection
   * @description Method will be invoked When user clicks + for object Data type and Data type is collection
   * @param field
   */
  const addChildForObjectDTandTypeIsCollection = (field: Field) => {
    (field.type as Array<TypeNode>).forEach((val) => {
      if (typeof val === 'object') {
        const item = val;
        if (item.type && !Array.isArray(item.type) && item.type === 'record') {
          item.fields = item.fields ? item.fields : [];
          const newProperty: Field = {
            id: uuid(),
            name: '',
            type: 'null',
            default: null
          };
          item.fields.push(newProperty);
        }
      }
    });
  };

  /**
   * @method removePropertyClickHandler
   * @description remove property when user clicks X
   * @param fieldId
   * @param parentField
   * @returns
   */
  const removePropertyClickHandler = (fieldId: string, parentField: Field | null, curField: Field) => {
    if (parentField === null) {
      return;
    }
    const updatedFields = [...fields];
    removeEncryptedField(curField);
    if (parentField && parentField.type && parentField.type === 'record' && parentField.fields) {
      const index = parentField.fields.findIndex((prop) => prop.id === fieldId);
      if (parentField.fields.length === 1) {
        delete parentField.fields;
      } else {
        parentField.fields.splice(index, 1);
      }
    } else if (parentField && typeof parentField.type === 'object' && !Array.isArray(parentField.type) && parentField.type.fields !== undefined) {
      const index = parentField.type.fields.findIndex((prop) => prop.id === fieldId);
      if (index !== -1) {
        if (parentField.type.fields.length === 1) {
          delete parentField.type.fields;
        } else {
          parentField.type.fields.splice(index, 1);
        }
      }
    } else if (Array.isArray(parentField.type)) {
      rmPropForTypeIsCollection(fieldId, parentField);
    }
    updateFields(updatedFields);
  };

  /**
   * @method rmPropForTypeIsCollection
   * @description Method to remove the property if Type is collection(collection of Types)
   * @param fieldId
   * @param parentField
   */
  const rmPropForTypeIsCollection = (fieldId: string, parentField: Field) => {
    const updatedFields = [...fields];
    (parentField.type as Array<TypeNode>).forEach((typeField) => {
      if (typeField.type === 'record') {
        const index = typeField.fields?.findIndex((prop: Field) => prop.id === fieldId);
        if (index !== undefined && index !== -1) {
          if (typeField.fields?.length === 1) {
            delete typeField.fields;
          } else {
            typeField.fields?.splice(index, 1);
          }
          updateFields(updatedFields);
        }
      }
    });
  };

  /**
   *
   * @method fieldValidation
   * @param regExp
   * @param fieldValue
   * @description Method will be invoked when user changes data in namespace or field name
   * @returns true if invalid
   */
  const fieldValidation = (fieldValue: string, regExp: RegExp) => {
    if (fieldValue.length > 0) {
      if (!regExp.test(fieldValue)) {
        return true;
      }
      return false;
    }
    return false;
  };

  /**
  * @method namespaceValidation
  * @returns true if invalid
  */
  const fieldValueValidation = (fieldValue: string, key: string) => {
    if (key === 'namespace') {
      if (!fieldValue.length) {
        setIsNamespaceInvalid(true);
        setIsNamespaceRequired(true);
      } else {
        setIsNamespaceRequired(false);
        setIsNamespaceInvalid(fieldValidation(fieldValue, namespaceAndFieldNameRegex));
      }
    } else if (key === 'name') {
      if (!fieldValue.length) {
        setIsFieldNameInvalid(true);
        setIsFieldNameRequired(true);
      } else {
        setIsFieldNameRequired(false);
        setIsFieldNameInvalid(fieldValidation(fieldValue, namespaceAndFieldNameRegex));
      }
    }
  };

  /**
   * @method removeEncryptedField
   * @description Removes encrypted fields from the given fieldTypeObj.
   * @param fieldTypeObj - The object representing the field type.
   */
  const removeEncryptedField = (fieldTypeObj: TypeNode) => {
    arrOfEncryptedData(fieldTypeObj.name || '', false);
    if (typeof fieldTypeObj.type === 'object' && !Array.isArray(fieldTypeObj.type)) {
      const fields = fieldTypeObj.type.fields || [];
      (fields as Array<TypeNode>).forEach((recNode) => {
        removeEncryptedField(recNode);
      });
    } else if (typeof fieldTypeObj.type === 'object' && Array.isArray(fieldTypeObj.type)) {
      (fieldTypeObj.type as Array<TypeNode>).forEach((recNodes) => {
        if (typeof recNodes === 'object' && recNodes.fields) {
          const fields = recNodes.fields || [];
          (fields as Array<TypeNode>).forEach((recNode) => {
            removeEncryptedField(recNode);
          });
        }
      });
    }
  };

  /**
   * @method isChildFieldExists
   * @description Method will validate given field has any child or not
   * @param field
   * @returns Boolean
   */
  const isChildFieldExists = (field: Field) => {
    let isBlnChildFieldExists = false;
    if (Utils.checkTypes(field.type, 'record')) {
      if (typeof field.type === 'object' && !Array.isArray(field.type) && field.type.fields && field.type.fields.length > 0) {
        isBlnChildFieldExists = true;
      } else if (Array.isArray(field.type)) {
        const filteredObj = field.type.filter((item) => (typeof item === 'object' && !Array.isArray(item.type) && item.type === 'record' && item.fields && item.fields.length));
        if (filteredObj.length) {
          isBlnChildFieldExists = true;
        }
      }
    } else if (Utils.checkTypes(field.type, 'array')) {
      isBlnChildFieldExists = true;
    }

    return isBlnChildFieldExists;
  };

  /**
   * @method popover
   * @description Renders a popover component for a given field.
   * @param field - The field object.
   * @returns The popover component.
   */
  const popover = (field: Field) => (
    <Popover id="popover-basic" placement="right" className="data-list-popover">
      <Popover.Body className="data-list-popover-body">
        <DataTypesList
          field={field}
          arrayChildDataTypeClick={arrayChildDataTypeClickHandler}
          numberChildDataTypeClick={numberChildDataTypeClickHandler}
          arrayNumberChildDataTypeClick={arrayNumberChildDataTypeClickHandler}
          dataTypeClick={(dataType, field) => dataTypeClickHandler(dataType, field)}
        />
      </Popover.Body>
    </Popover>
  );

  /**
   * @method renderSelectDataType
   * @description Determines whether to render the select data type component.
   * @returns {boolean} Returns true if the select data type component should be rendered, false otherwise.
   */
  const renderSelectDataType = () => {
    if (parentField && Utils.checkTypes(parentField.type, 'array')) {
      return false;
    }
    return true;
  };

  /**
   * @method renderRemoveBtn
   * @description Determines whether to render the remove button.
   * @returns {boolean} True if the remove button should be rendered, false otherwise.
   */
  const renderRemoveBtn = () => {
    if (!parentField || Utils.checkTypes(parentField.type, 'array')) {
      return false;
    }
    return true;
  };

  /**
   * @method encryptDataHandleCheck
   * @description Handles the check event for encrypting data.
   * @param fieldName - The field name.
   */
  const encryptDataHandleCheck = (fieldName: any) => {
    const { id, checked } = fieldName.target;
    arrOfEncryptedData(id, checked);
  };

  /**
   * @description: This method checks if the field type is either a string or a string with null. If the field type includes any other data type( integer, boolean, array, or record), even if it is combined with a string, the method will return false.
   * @returns Boolean True| false
   */
  const isValidFieldForEncrypt = () => {
    if (Array.isArray(field.type)) {
      return (field.type.length === 2 && Utils.checkTypes(field.type, DTYPE_STRING) && Utils.checkTypes(field.type, DTYPE_NULL));
    }
    if (field.type === DTYPE_STRING) {
      return true;
    }
    return false;
  };

  function checkFieldDisableCondition(field: any) {
    if (!Array.isArray(field.type)) {
      if (field.type !== 'record') {
        return true;
      }
    } else if (!field.type.includes('null')) {
      return true;
    }
    return false;
  }

useEffect(() => {
  let shouldDisableConfirm = false;
  let hasDataTypeError = false;
  if (addChildElemBtnEnable() || isFieldNameInvalid || isNamespaceInvalid || isSensitiveFieldExist) {
      shouldDisableConfirm = true;
  }

  if (checkFieldDisableCondition(field)) {
    shouldDisableConfirm = true;
    hasDataTypeError = true;
  }

  // Process each field
  const processFields = (fields: any) => {
    fields.forEach((field: any) => {
      if (checkFieldDisableCondition(field)) {
        shouldDisableConfirm = true;
      }
      if (Array.isArray(field.type)) {
        field.type.forEach(processFieldItem);
      }
    });
  };

  // Process individual field items
  const processFieldItem = (item: any) => {
    if (typeof item === 'object') {
      if (item.fields) {
        processFields(item.fields);
      } else if (item.type === 'array' && item.items.isArray) {
        item.items.forEach(processFieldItem);
      }
    }
  };

  // Start processing if fields are valid
  if (Array.isArray(fields) && fields.length && fields[0].fields?.length) {
    processFields(fields[0].fields);
  }

  // Apply the results
  disableAvroschemaConfirm(shouldDisableConfirm);
  setDataTypeError(hasDataTypeError);
}, [field.type, field.namespace, field.name, isFieldNameInvalid, isNamespaceInvalid, isSensitiveFieldExist]);

  return (
    <>
      {
        ('namespace' in field)
        && (
          <div className="row-container">
            <div className="child-connector" />
            <input
              type="text"
              defaultValue={field.namespace}
              name="namespace"
              onBlur={(e) => handleLabelChange(field, e.target.value, 'namespace')}
              onChange={(e) => fieldValueValidation(e.target.value, 'namespace')}
              placeholder={translate('service_validation_error.txt_namespace_place') ?? ''}
              data-testid="namespace"
            />
            {isNamespaceInvalid && (<span className="error-msg">{isNamespaceRequired ? translate('schemaGeneratorText.requiredNamespace') : translate('schemaGeneratorText.invalidNamespace')}</span>)}
          </div>)
      }
      <div className="row-container">
        {
          (Utils.checkTypes(field.type, 'record') || Utils.checkTypes(field.type, 'array')) ? (
            <>
              <div className="child-connector-root" />
              <div>
                {Utils.checkTypes(field.type, 'record') && <button type="button" className={(addChildElemBtnEnable() || isFieldNameInvalid || isNamespaceInvalid) ? 'add-child-prop-elem add-child-button-disabled' : 'add-child-prop-elem'} onClick={() => objectAddChildClickHandler(field)} disabled={addChildElemBtnEnable() || isFieldNameInvalid || isNamespaceInvalid} data-testid="btn-add-field">+</button>}
                {Utils.checkTypes(field.type, 'array') && <button type="button" className="add-child-prop-elem add-child-button-disabled">+</button>}
              </div>
              {isChildFieldExists(field) ? (
                <Button variant="tertiary" aria-label="add-child" type="button" className="btn-default button-caret-container" onClick={(e) => toggleChildren(e, field.id)}>
                  <i className="bi bi-caret-down-fill" />
                </Button>
              ) : (
                <Button variant="tertiary" aria-label="add-child" type="button" className="btn-default button-caret-container button-caret-container-disabled">
                  <i className="bi bi-caret-right-fill" />
                </Button>)}
            </>
          ) : (
            <div className="child-connector" />
          )
        }
        <div>
          <input
            type="text"
            defaultValue={field.name}
            name={`field-name-${field.id}`}
            onBlur={(e) => handleLabelChange(field, e.target.value, 'name')}
            placeholder={translate('service_validation_error.txt_field_name') ?? ''}
            onChange={(e) => fieldValueValidation(e.target.value, 'name')}
            data-testid="field-name"
          />
        </div>
        {isCheckClicked && (
          <Checkbox
            type="checkbox"
            id={field.name}
            data-testid="encryptCheckBox"
            disabled={!isValidFieldForEncrypt()}
            // className="encryptCheckBox"
            label="Encrypt"
            checked={encryptDataArr.includes(field.name) && isValidFieldForEncrypt()}
            onChange={encryptDataHandleCheck}
          />)}
        {renderSelectDataType() && !('namespace' in field) && (
          <Button variant="primary" className="select-data-type" onClick={() => setIsDatatypeBtnClick(!isDatatypeBtnClick)} data-testid="select-datatype-btn">{translate('schemaGeneratorText.dataType')}</Button>
        )}
        {isDatatypeBtnClick && popover(field)}
        {!isDatatypeBtnClick && renderRemoveBtn() && (
          <div>
            <Button variant="tertiary" type="button" className="property-remove-btn" onClick={() => removePropertyClickHandler(field.id, parentField, field)} data-testid="btn-delete-field">
              <MyIcon name="deleteIcon" className="datatype-delete-icon" />
            </Button>
          </div>
        )}
        {isFieldNameInvalid && (<span className="error-msg fieldname-error-msg">{isFieldNameRequired ? translate('schemaGeneratorText.requiredFieldname') : translate('schemaGeneratorText.invalidFieldname')}</span>)}
        {dataTypeError && (<span className="error-msg fieldname-error-msg">{translate('schemaGeneratorText.dataTypeError')}</span>)}
      </div>
    </>
  );
};

export default FieldElemContainer;
